import { useEffect, useState } from 'react';
import { Button, Modal } from '@react-apps/design-system';
import { Textarea } from '@/shared/components/textarea';
import { describeFileError } from '../format';
import { useForceDeleteFile } from '../hooks/use-stored-files';

const MAX_REASON = 500;

/**
 * Apagar é irreversível e vai para a auditoria, por isso o motivo é
 * obrigatório (Decisão 3 do épico files-admin). O erro fica no modal, que
 * continua aberto para tentar de novo.
 */
export function ForceDeleteFileModal({
  fileId,
  legalHold,
  open,
  onClose,
  onDeleted,
}: {
  fileId: string;
  legalHold: boolean;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useForceDeleteFile(fileId);

  useEffect(() => {
    if (open) {
      setReason('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = reason.trim();
  const tooLong = trimmed.length > MAX_REASON;
  const blocked = legalHold || trimmed === '' || tooLong || mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Apagar arquivo"
      description="Apaga o objeto do bucket e marca o registro como apagado. Não tem volta."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            onClick={() => mutation.mutate(trimmed, { onSuccess: onDeleted })}
            disabled={blocked}
          >
            {mutation.isPending ? 'Apagando…' : 'Apagar arquivo'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {legalHold && (
          <p className="text-(--color-text-primary) text-sm">
            O legal hold deste arquivo está ligado. Desligue o legal hold antes
            de apagar.
          </p>
        )}
        {mutation.isError && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {describeFileError(
              mutation.error,
              'O arquivo não foi apagado. Tente de novo.',
            )}
          </p>
        )}
        <Textarea
          label="Motivo (obrigatório)"
          value={reason}
          onChange={setReason}
          rows={3}
          helperText={
            tooLong
              ? `O motivo passou de ${MAX_REASON} caracteres.`
              : 'Fica na auditoria, junto com quem apagou. Não escreva nome, documento ou e-mail.'
          }
        />
      </div>
    </Modal>
  );
}
